import { useState, useEffect } from 'react';
import { Settings, Save } from 'lucide-react';
import { opsApi } from '../../services/ops-api';
import { useOpsStore } from '../../store/ops.store';
import type { CapacityFormula } from '../../types/ops';


const OPTIONS: { value: CapacityFormula; label: string; formula: string; description: string }[] = [
  {
    value: 'full',
    label: 'CapacityFull',
    formula: 'TimeSlots × MaxParticipants × Số ngày',
    description: 'Tính capacity tối đa dựa trên số slot cấu hình, số người tối đa và số ngày trong kỳ. Không trừ các slot đã bị khoá.',
  },
  {
    value: 'subtractBlock',
    label: 'CapacitySubtractBlock (Mặc định)',
    formula: 'CapacityFull − SUM(SlotTaken từ Booking Blocks)',
    description: 'Tính capacity tối đa rồi trừ đi số slot bị khoá bởi Booking Block. Phản ánh capacity thực tế có thể bán.',
  },
];

export default function CapacityFormulaSelector({ isDark }: { isDark: boolean }) {
  const config = useOpsStore((s) => s.config);
  const savedFormula = config?.capacityFormula || 'subtractBlock';

  const [formula, setFormula] = useState<CapacityFormula>(savedFormula);
  const [saving, setSaving] = useState(false);

  // Sync local state when store config changes (e.g. after fetch)
  useEffect(() => {
    setFormula(savedFormula);
  }, [savedFormula]);

  const hasChanges = formula !== savedFormula;

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await opsApi.saveConfig({ capacityFormula: formula });
      // Persist to localStorage so it survives browser reload
      try { localStorage.setItem('ops:capacityFormula', formula); } catch { /* noop */ }
      // Invalidate all cached views so they re-fetch with new capacity formula
      // and update the config in the store
      useOpsStore.setState({ fetchedKeys: {}, config: updated });
      // Switch to executive view and fetch immediately
      useOpsStore.getState().setActiveView('executive');
      await useOpsStore.getState().fetchDashboardData(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-lg p-5 space-y-3 ${isDark ? 'bg-slate-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <h3 className={`flex items-center gap-2 text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
        <Settings className="h-5 w-5 text-blue-400" />
        Công thức Capacity
      </h3>
      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
        Chọn cách tính capacity cho Scorecards và Occupancy Breakdown.
      </p>
      <div className="space-y-2">
        {OPTIONS.map(opt => (
          <label
            key={opt.value}
            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              formula === opt.value
                ? 'border-cyan-500/60 bg-cyan-500/10'
                : isDark ? 'border-slate-600 hover:border-slate-500' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="capacityFormula"
              value={opt.value}
              checked={formula === opt.value}
              onChange={() => setFormula(opt.value)}
              disabled={saving}
              className="accent-cyan-500 mt-0.5"
            />
            <div>
              <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{opt.label}</div>
              <div className="text-xs text-cyan-400/80 mt-0.5 font-mono">{opt.formula}</div>
              <div className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{opt.description}</div>
            </div>
          </label>
        ))}
      </div>
      <button
        onClick={handleSave}
        disabled={!hasChanges || saving}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
          hasChanges
            ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:shadow-lg hover:shadow-cyan-500/25 cursor-pointer'
            : isDark ? 'bg-slate-700 text-slate-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
      >
        <Save className="w-4 h-4" />
        {saving ? 'Đang lưu...' : 'Lưu & chạy lại báo cáo'}
      </button>
    </div>
  );
}
